/**
 * FTS5 query construction — the injection defence.
 *
 * A raw SQLite FTS5 `MATCH` right-hand side is **not** protected by parameter binding.
 * Binding stops the *SQL* parser from seeing user text, but FTS5 then parses the bound
 * string with its own grammar, which has its own operators: double-quoted phrases,
 * `*` prefixes, `^` column-start anchors, `AND` / `OR` / `NOT`, `NEAR(… , n)` and
 * column filters (`title : x`, `{title performer} : x`). A user typing
 * `{caption} : dmca` into the search box is a query-rewrite attack on a bound
 * parameter, and a user typing `song AND` is a 500.
 *
 * The defence here is total containment rather than blacklisting:
 *
 *  1. The input is reduced by `normalizeText()` to `[\p{L}\p{N}]`-runs separated by
 *     single spaces, so no FTS5 metacharacter survives tokenisation at all.
 *  2. Every token is then emitted as a double-quoted FTS5 *string*, with any `"`
 *     doubled (`fts5Quote`). Inside a quoted string FTS5 has no operators; the
 *     tokenizer simply splits it into a phrase.
 *  3. Structure — `(`, `)`, `*`, `AND`, `OR` and the column-filter braces — is emitted
 *     only by this module, never copied from the input.
 *  4. Column names come from `FTS_COLUMNS`, a frozen allowlist. A caller-supplied
 *     column outside it throws; user input is never consulted.
 *
 * Grammar facts below were verified by executing them against a real `node:sqlite`
 * FTS5 table (`unicode61 remove_diacritics 2`, `prefix='2 3 4'`), not read from docs:
 *
 *  - `("a" OR "b")*` is `fts5: syntax error near "*"` — a prefix `*` must sit on an
 *    individual quoted term, never on a parenthesised group. Hence the star is
 *    attached per-term inside the clause.
 *  - A column filter binds to the single phrase or parenthesised group that follows
 *    it: `{title} : ("praise") AND ("sizzla")` matched a row whose only "sizzla" is in
 *    `performer`. The whole conjunction must therefore be wrapped in one group —
 *    `{title} : ( … )` — or the filter silently leaks the other columns.
 *  - `MATCH ''` throws `fts5: syntax error near ""`. That is why the empty case
 *    returns `null` (a sentinel a caller must branch on) and never `''` (a string a
 *    caller would happily bind).
 *  - `("")` and `(""*)` are legal and match nothing, so a degenerate token cannot
 *    break the expression even if one ever reached us.
 *
 * This module is **pure**: no `node:*` import, no I/O, no `Date`, no randomness. It is
 * shared verbatim by the API, the indexer and the web client.
 */
import { normalizeText } from './normalize.js';
import { translit } from './translit.js';
/**
 * The hardcoded column allowlist. Order matches the `tracks_fts` declaration in
 * `spec.md` §5.3, which is also the order the `bm25()` weights in §6.5 step 3 are
 * given in — keep the two in step.
 */
export const FTS_COLUMNS = Object.freeze([
    'title',
    'performer',
    'album',
    'file_name',
    'caption',
    'search_key',
]);
const FTS_COLUMN_SET = new Set(FTS_COLUMNS);
/** Default ceiling on how many tokens of the user's query are used. */
export const DEFAULT_MAX_TOKENS = 8;
/** Hard ceiling on `maxTokens`, whatever a caller asks for. */
export const MAX_TOKENS_CAP = 32;
/** Shortest token that may carry a prefix `*`. */
export const MIN_PREFIX_LENGTH = 2;
/** `true` for the six names in {@link FTS_COLUMNS}, `false` for everything else. */
export function isFtsColumn(value) {
    return FTS_COLUMN_SET.has(value);
}
/**
 * Escape a token as an FTS5 double-quoted string.
 *
 * Inside such a string FTS5 recognises no operators, so this — and not any kind of
 * character filtering — is what makes an arbitrary token inert. The only character
 * with meaning is `"`, which terminates the string and is escaped by doubling.
 */
export function fts5Quote(token) {
    return '"' + token.replace(/"/g, '""') + '"';
}
function clampMaxTokens(value) {
    if (value === undefined || !Number.isFinite(value))
        return DEFAULT_MAX_TOKENS;
    const floored = Math.floor(value);
    if (floored < 1)
        return 1;
    if (floored > MAX_TOKENS_CAP)
        return MAX_TOKENS_CAP;
    return floored;
}
/**
 * `normalizeText` collapses every non-letter/non-digit run to a single space, so
 * splitting on a space is the whole tokenizer. The `filter` guards against a leading
 * or trailing space rather than trusting `normalizeText` to have trimmed.
 */
function tokenize(userInput, maxTokens) {
    const normalized = normalizeText(userInput);
    if (normalized.length === 0)
        return [];
    const tokens = normalized.split(' ').filter((token) => token.length > 0);
    return tokens.length > maxTokens ? tokens.slice(0, maxTokens) : tokens;
}
/**
 * A token plus its transliteration, deduplicated.
 *
 * The transliteration is re-normalised because the maps in `translit.ts` can emit
 * multi-character expansions (`щ→shch`, `x→кс`) and we must never hand an
 * un-normalised string to `fts5Quote` — a token and its index entry have to have gone
 * through the identical pipeline or the match silently fails.
 */
function variantsFor(token) {
    const alternate = normalizeText(translit(token));
    if (alternate.length === 0 || alternate === token)
        return [token];
    return [token, alternate];
}
function clauseFor(token, isLast, usePrefix) {
    // The gate is the *token's* length, not each variant's: for the last token `x` the
    // transliteration `кс` is two characters, and starring it while the token itself
    // stays exact would make `x` behave like a prefix query in one script only. The
    // per-variant check below is then belt-and-braces for a variant that came out
    // shorter than its token.
    const withPrefix = usePrefix && isLast && token.length >= MIN_PREFIX_LENGTH;
    const variants = variantsFor(token);
    const terms = variants.map((variant) => withPrefix && variant.length >= MIN_PREFIX_LENGTH
        ? fts5Quote(variant) + '*'
        : fts5Quote(variant));
    // A single term is already atomic: `"a" AND "b"` needs no parentheses and FTS5's
    // NOT > AND > OR precedence cannot mis-bind it. Two or more must be grouped, or the
    // OR would escape the conjunction.
    return terms.length > 1 ? '(' + terms.join(' OR ') + ')' : terms.join('');
}
function resolveColumns(columns) {
    if (columns === undefined)
        return [];
    const resolved = [];
    for (const column of columns) {
        // Defensive at runtime as well as in the type: this is the one place a column name
        // could ever reach the SQL, so a wrong value must be loud, not quoted-and-passed.
        if (typeof column !== 'string' || !isFtsColumn(column)) {
            throw new RangeError(`buildMatchQuery: column ${JSON.stringify(column)} is not in the FTS5 allowlist ` +
                `[${FTS_COLUMNS.join(', ')}]`);
        }
        if (!resolved.includes(column))
            resolved.push(column);
    }
    return resolved;
}
/**
 * Build the right-hand side of an FTS5 `MATCH` from untrusted user input.
 *
 * Returns `null` — never `''` — when the input normalises to zero tokens, because an
 * empty `MATCH` string is a SQLite error, not an empty result set. Callers must branch
 * on `null` and return an empty page without touching the database (`spec.md` §5.4
 * rule 3, §6.5 step 1).
 *
 * Shape of the output (the transliterated variant is whatever `translit.ts` yields —
 * shown here illustratively, and absent whenever it equals the token):
 *
 * ```text
 * buildMatchQuery('vybz kart')          -> ("vybz" OR …) AND ("kart"* OR …)
 * buildMatchQuery('2024')               -> "2024"*
 * buildMatchQuery('x', {columns:['title']}) -> {title} : ("x" OR …)
 * ```
 *
 * @throws RangeError if `columns` contains a name outside {@link FTS_COLUMNS}.
 */
export function buildMatchQuery(userInput, options = {}) {
    // Column validation runs first and unconditionally: a bad column is a programmer
    // error and must not be masked by an input that happens to normalise to nothing.
    const columns = resolveColumns(options.columns);
    const usePrefix = options.prefix ?? true;
    const tokens = tokenize(userInput, clampMaxTokens(options.maxTokens));
    if (tokens.length === 0)
        return null;
    const lastIndex = tokens.length - 1;
    const clauses = tokens.map((token, index) => clauseFor(token, index === lastIndex, usePrefix));
    const expression = clauses.join(' AND ');
    // The filter must wrap the *entire* conjunction. Verified: `{title} : ("a") AND ("b")`
    // applies the filter to `"a"` only and matches `"b"` in any column.
    return columns.length > 0 ? '{' + columns.join(' ') + '} : (' + expression + ')' : expression;
}
/**
 * Escape a value for use with `LIKE … ESCAPE '\'`.
 *
 * `%`, `_` and the escape character itself are the only LIKE metacharacters. The single
 * character class handles all three in one pass, so a literal backslash cannot be
 * double-escaped by a later replacement.
 */
export function likeEscape(value) {
    return value.replace(/[\\%_]/g, (character) => '\\' + character);
}
/**
 * The `LIKE` fallback used when the startup probe reports no FTS5 (`spec.md` §6.5
 * step 5). Substring match over one column, ANDed across tokens.
 *
 * The predicate is built from a fixed shape and a validated column name; every piece
 * of user text leaves through `params` as a bind value.
 *
 * For `'vybz kartel'` the predicate is
 * `(search_key LIKE ? ESCAPE '\' AND search_key LIKE ? ESCAPE '\')` with params
 * `['%vybz%', '%kartel%']`.
 *
 * @throws RangeError if `column` is outside {@link FTS_COLUMNS}.
 */
export function buildLikeClauses(userInput, maxTokens = DEFAULT_MAX_TOKENS, column = 'search_key') {
    if (typeof column !== 'string' || !isFtsColumn(column)) {
        throw new RangeError(`buildLikeClauses: column ${JSON.stringify(column)} is not in the FTS5 allowlist ` +
            `[${FTS_COLUMNS.join(', ')}]`);
    }
    const tokens = tokenize(userInput, clampMaxTokens(maxTokens));
    if (tokens.length === 0)
        return { where: '0', params: [] };
    // Single-quoted SQL string literal for the escape character: `node:sqlite` treats a
    // double-quoted string as an identifier (`spec.md` §5.4 rule 4).
    const predicate = column + " LIKE ? ESCAPE '\\'";
    return {
        where: '(' + tokens.map(() => predicate).join(' AND ') + ')',
        params: tokens.map((token) => '%' + likeEscape(token) + '%'),
    };
}
//# sourceMappingURL=fts.js.map